/**
 * Dashboard Analytics Hooks
 *
 * Custom React Query hooks for fetching dashboard analytics data
 */

import { useQuery } from '@tanstack/react-query';
import dashboardApiService from '../api/dashboardApiService';

/**
 * Hook to fetch health trend data
 * @param {number} days - Number of days to look back
 * @param {string} connectionId - Optional connection ID filter
 * @param {object} options - React Query options
 */
export const useHealthTrend = (days = 30, connectionId = null, options = {}) => {
  return useQuery({
    queryKey: ['healthTrend', days, connectionId],
    queryFn: () => dashboardApiService.getHealthTrend(days, connectionId),
    staleTime: 60000, // 1 minute
    ...options
  });
};

/**
 * Hook to fetch issues summary with month-over-month comparison
 * @param {string} month - 'current' or 'YYYY-MM'
 * @param {object} options - React Query options
 */
export const useIssuesSummary = (month = 'current', options = {}) => {
  return useQuery({
    queryKey: ['issuesSummary', month],
    queryFn: () => dashboardApiService.getIssuesSummary(month),
    staleTime: 60000, // 1 minute
    ...options
  });
};

/**
 * Hook to fetch connection heatmap data
 * @param {object} options - React Query options
 */
export const useConnectionHeatmap = (options = {}) => {
  return useQuery({
    queryKey: ['connectionHeatmap'],
    queryFn: () => dashboardApiService.getConnectionHeatmap(),
    staleTime: 300000, // 5 minutes
    ...options
  });
};

/**
 * Hook to fetch top failing validations
 * @param {number} limit - Number of validations to return
 * @param {number} days - Number of days to look back
 * @param {object} options - React Query options
 */
export const useTopFailingValidations = (limit = 10, days = 30, options = {}) => {
  return useQuery({
    queryKey: ['topFailingValidations', limit, days],
    queryFn: () => dashboardApiService.getTopFailingValidations(limit, days),
    staleTime: 60000, // 1 minute
    ...options
  });
};

/**
 * Hook to fetch ROI metrics
 * @param {number} days - Number of days to look back
 * @param {object} options - React Query options
 */
export const useROIMetrics = (days = 90, options = {}) => {
  return useQuery({
    queryKey: ['roiMetrics', days],
    queryFn: () => dashboardApiService.getROIMetrics(days),
    staleTime: 300000, // 5 minutes (ROI data changes less frequently)
    ...options
  });
};
